import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd, removeWillChangeOnAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 * @property {HTMLDivElement} menuDrawer - The slideable drawer panel containing the menu.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details', 'menuDrawer'];

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keyup', this.#onKeyUp);
    this.#setupAnimatedElementListeners();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
  }

  /**
   * Close the main menu drawer when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    this.#close(this.#getDetailsElement(event));
  };

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest('details') ?? this.refs.details;
  }

  /**
   * Incremented on every open/close so a pending (animation-delayed) open or
   * reset from an earlier click can tell it has been superseded.
   */
  #transitionId = 0;

  /**
   * Toggle the main menu drawer. The `open` attribute is managed here instead
   * of by the native <summary> toggle: letting both run raced with the delayed
   * reset after a close, which made some clicks do nothing.
   * @param {Event} [event]
   */
  toggle(event) {
    event?.preventDefault();
    return this.refs.details.classList.contains('menu-open') ? this.close() : this.open();
  }

  /**
   * Open the closest drawer or the main menu drawer
   * @param {string} [target]
   * @param {Event} [event]
   */
  open(target, event) {
    const details = this.#getDetailsElement(event);
    const summary = details.querySelector('summary');

    if (!summary) return;

    const transitionId = ++this.#transitionId;
    const isMainDrawer = details === this.refs.details;

    if (isMainDrawer) {
      details.setAttribute('open', '');
      this.#pinHeader();
      this.#updateDrawerTop();
    }

    summary.setAttribute('aria-expanded', 'true');

    this.preventInitialAccordionAnimations(details);
    requestAnimationFrame(() => {
      // Closed again before this frame: don't re-open.
      if (transitionId !== this.#transitionId) return;

      details.classList.add('menu-open');

      if (target) {
        this.refs.menuDrawer.classList.add('menu-drawer--has-submenu-opened');
      }

      // Wait for the drawer animation to complete before trapping focus
      const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');
      onAnimationEnd(drawer || details, () => trapFocus(details), { subtree: false });
    });
  }

  /**
   * Close the drawer and open the theme's predictive search modal. The drawer
   * traps focus, so it has to close before the search dialog takes focus.
   */
  openSearch() {
    const searchModal = /** @type {(HTMLElement & { showDialog?: () => void }) | null} */ (
      document.getElementById('search-modal')
    );
    this.close();
    // `close()` only releases the focus trap after its slide-out animation;
    // release it now so the dialog's input can receive focus.
    removeTrapFocus();
    searchModal?.showDialog?.();
  }

  /** @returns {HTMLElement | null} */
  get #header() {
    return this.closest('#header-component') ?? this.closest('header');
  }

  /**
   * Keep the header on screen while the drawer is open. When it has scrolled
   * (partly) out of view or is hidden by the scroll-up sticky mode, pin it to
   * the top of the viewport; see `[data-drawer-pinned]` in header.liquid.
   */
  #pinHeader() {
    const header = this.#header;
    if (!header) return;

    const isOffscreen = header.getBoundingClientRect().top < -1;
    const isHidden = header.dataset.stickyState === 'idle';
    header.toggleAttribute('data-drawer-pinned', isOffscreen || isHidden);
  }

  #unpinHeader() {
    this.#header?.removeAttribute('data-drawer-pinned');
  }

  /**
   * Position the drawer (and its backdrop) right below the header so it never
   * overlaps it. Measured on open (after pinning) because the header height
   * changes with scroll state, announcement bars and viewport width.
   */
  #updateDrawerTop() {
    const header = this.#header;
    const headerBottom = header ? header.getBoundingClientRect().bottom : 0;
    // The header wrapper can report a collapsed rect (height 0) when its
    // children are sticky/fixed; the hamburger's own bottom edge is then the
    // reliable stand-in for the header's bottom.
    const summary = this.refs.details.querySelector('summary');
    const summaryBottom = summary ? summary.getBoundingClientRect().bottom : 0;
    const bottom = Math.max(0, Math.round(Math.max(headerBottom, summaryBottom)));
    this.style.setProperty('--menu-drawer-top', `${bottom}px`);
  }

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Close the main menu drawer
   */
  close() {
    this.#close(this.refs.details);
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const summary = details.querySelector('summary');

    if (!summary) return;

    const transitionId = ++this.#transitionId;

    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');
    this.refs.menuDrawer.classList.remove('menu-drawer--has-submenu-opened');

    // Wait for the .menu-drawer element's transition, not the entire details subtree
    // This avoids waiting for child accordion/resource-card animations which can cause issues on Firefox
    const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');

    onAnimationEnd(
      drawer || details,
      () => {
        // Re-opened before the close animation finished: keep it open.
        if (transitionId !== this.#transitionId) return;

        reset(details);
        if (details === this.refs.details) {
          this.#unpinHeader();
          removeTrapFocus();
          const openDetails = this.querySelectorAll('details[open]:not(accordion-custom > details)');
          openDetails.forEach(reset);
        } else {
          trapFocus(this.refs.details);
        }
      },
      { subtree: false }
    );
  }

  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation
   * to remove the stacking context and allow submenus to be positioned correctly
   */
  #setupAnimatedElementListeners() {
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }

  /**
   * Temporarily disables accordion animations to prevent unwanted transitions when the drawer opens.
   * Adds a no-animation class to accordion content elements, then removes it after 100ms to
   * re-enable animations for user interactions.
   * @param {HTMLDetailsElement} details - The details element containing the accordions
   */
  preventInitialAccordionAnimations(details) {
    const content = details.querySelectorAll('accordion-custom .details-content');

    content.forEach((element) => {
      if (element instanceof HTMLElement) {
        element.classList.add('details-content--no-animation');
      }
    });
    setTimeout(() => {
      content.forEach((element) => {
        if (element instanceof HTMLElement) {
          element.classList.remove('details-content--no-animation');
        }
      });
    }, 100);
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}